import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * A custom element that shows a compact "Added to Bag" confirmation after a
 * successful add-to-cart, as an alternative to auto-opening the full cart
 * drawer. "View cart" opens the existing cart-drawer via its declarative
 * `on:click="#cart-drawer/open"` action; "Check out" submits the standard cart
 * form straight to Shopify checkout. Ported from hagear.shop (Oct 24, 2026).
 *
 * @typedef {object} CartAddedPopupRefs
 * @property {HTMLElement} panel
 * @property {HTMLImageElement} [image]
 * @property {HTMLElement} title
 * @property {HTMLElement} [variant]
 * @property {HTMLElement} price
 * @property {HTMLElement} viewBagCount
 * @property {HTMLTemplateElement} moneyFormat
 * @extends {Component<CartAddedPopupRefs>}
 */
class CartAddedPopupComponent extends Component {
  requiredRefs = ['panel', 'title', 'price', 'viewBagCount', 'moneyFormat'];

  /** @type {number | undefined} */
  #dismissTimeout;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    // "View cart" opens the drawer; get out of its way.
    this.querySelector('.cart-added-popup__view-bag')?.addEventListener('click', () => this.close(), { signal });
    // Tapping the dimmed background also closes it.
    this.querySelector('.cart-added-popup__scrim')?.addEventListener('click', () => this.close(), { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    if (this.#dismissTimeout) clearTimeout(this.#dismissTimeout);
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    if (event.action !== 'add') return;

    // Ignore updates this component itself doesn't own the source of (e.g. quantity
    // changes inside the drawer), and skip when the add originated inside a modal
    // (quick add) — that dialog owns its own confirmation/focus flow.
    const sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;
    if (sourceModal) return;

    const addedLines = event.lines ?? [];
    if (addedLines.length === 0) return;

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;

        const items = detail?.items ?? [];
        // merchandiseId arrives as a GID (e.g. "gid://shopify/ProductVariant/123"),
        // while the AJAX cart's item.variant_id is the plain numeric id — compare
        // by the trailing numeric segment rather than the raw strings.
        const rawMerchandiseId = addedLines[addedLines.length - 1]?.merchandiseId;
        const matchedVariantId = rawMerchandiseId?.toString().match(/(\d+)$/)?.[1];
        const item = items.find(
          /** @param {any} cartItem */
          (cartItem) => cartItem.variant_id?.toString() === matchedVariantId
        );

        if (!item) return;

        const totalQuantity = items.reduce(/** @param {number} sum @param {any} cartItem */ (sum, cartItem) => sum + cartItem.quantity, 0);
        this.#populate(item, totalQuantity);
        this.#open();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-added-popup] Event promise rejected:', error);
      });
  };

  /**
   * @param {any} item - A single item from the AJAX cart response.
   * @param {number} cartItemCount
   */
  #populate(item, cartItemCount) {
    const { image, title, variant, price, viewBagCount } = this.refs;

    if (image) {
      if (item.image) {
        image.src = item.image.replace(/(\.[a-z0-9]+)(\?|$)/i, '_200x$1$2');
        image.alt = item.product_title ?? '';
        image.hidden = false;
      } else {
        image.hidden = true;
      }
    }

    title.textContent = item.product_title ?? '';

    if (variant) {
      const variantText = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      const quantityText = item.quantity > 1 ? `${variantText ? ' · ' : ''}Qty ${item.quantity}` : '';
      variant.textContent = variantText + quantityText;
      variant.hidden = !variantText && !quantityText;
    }

    const currency = this.dataset.currency ?? 'USD';
    price.textContent = this.#formatMoney(item.final_line_price ?? item.line_price ?? item.price * item.quantity, currency);
    viewBagCount.textContent = String(cartItemCount);
  }

  /**
   * @param {number} cents
   * @param {string} currency
   */
  #formatMoney(cents, currency) {
    const format = this.refs.moneyFormat.content.textContent || '{{amount}}';
    return formatMoney(cents, format, currency);
  }

  #open() {
    if (this.#dismissTimeout) clearTimeout(this.#dismissTimeout);

    this.hidden = false;
    // Allow the browser to register `hidden` removal before animating in.
    requestAnimationFrame(() => this.setAttribute('open', ''));

    this.#dismissTimeout = /** @type {number} */ (setTimeout(() => this.close(), 6000));
  }

  close() {
    if (this.#dismissTimeout) clearTimeout(this.#dismissTimeout);
    this.removeAttribute('open');
    setTimeout(() => {
      if (!this.hasAttribute('open')) this.hidden = true;
    }, 300);
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key === 'Escape') this.close();
  };
}

if (!customElements.get('cart-added-popup')) {
  customElements.define('cart-added-popup', CartAddedPopupComponent);
}
